/**
 * claude-rules.ts — The managed "SmartStack Git Safety" block that
 * `ss install` / `ss upgrade` merges into `~/.claude/CLAUDE.md`.
 *
 * The block is delimited by the BEGIN/END markers below so the merge is
 * idempotent and reversible: re-running install replaces the block in place,
 * `ss uninstall` strips it, and anything the user wrote OUTSIDE the markers is
 * never touched. Edit the rule text HERE (versioned), never in ~/.claude.
 *
 * Rationale: the git-guard PreToolUse hook is the HARD enforcement (it blocks
 * the command); this block is the SOFT layer — it tells Claude the rule up
 * front so it proposes instead of attempting. Defense in depth.
 */

export const CLAUDE_MD_MARKER_BEGIN =
  '<!-- BEGIN SmartStack Git Safety (managed by `ss install` — edit templates source, not here) -->';
export const CLAUDE_MD_MARKER_END = '<!-- END SmartStack Git Safety -->';

/** The rule text placed between the markers (the user's non-negotiable Git/EF rules). */
export const GIT_SAFETY_RULES = `## Règles Git — strictes et non négociables

ABSOLU : tu ne crées JAMAIS de branche, de commit, de push, de tag ou de
worktree sans une validation explicite de ma part dans le tour de
conversation courant. « Explicite » = je l'ai demandé en toutes lettres
dans mon dernier message. Une tâche qui touche du code n'est PAS une
autorisation implicite de versionner.

Interdits par défaut (refuse et demande confirmation avant) :
- \`git branch\`, \`git checkout -b\`, \`git switch -c\`
- \`git commit\` (même \`--amend\`), \`git push\`, \`git tag\`
- toute création de « shadow branch », ref hors \`refs/heads|remotes|tags\`,
  ou dépôt/checkpoint parallèle
- l'ouverture de PR ou tout artefact distant (GitHub/GitLab)

Si tu penses qu'un commit ou une branche serait utile : tu me le PROPOSES
en une phrase et tu ATTENDS mon « oui ». Tu ne l'exécutes pas en
anticipant ma réponse.

Ce que tu peux faire librement (lecture seule) :
- \`git status\`, \`git diff\`, \`git log\`, \`git branch -a\`, \`git for-each-ref\`
- inspecter l'état du repo pour te situer

> Renforcé par le hook \`PreToolUse\` \`git-guard\` (~/.claude/hooks/git-guard.cjs) :
> toute commande git d'écriture lancée via Bash est bloquée (exit 2) tant que tu
> n'as pas mon accord explicite.

## Migrations EF Core (contexte critique)

Mes migrations EF Core sont réparties sur plusieurs branches. Tu ne lances
JAMAIS \`dotnet ef migrations add/remove\` ni \`database update\` de ta propre
initiative. Une migration mal placée pollue l'historique et casse le merge.
Tu signales le besoin, je décide de la branche et du moment.

Un squash de migrations ne supprime JAMAIS une migration déjà présente dans
la branche de référence (la cible du merge : feature→develop ; develop,
release, hotfix→main). On ne consolide que ce qui est propre à la branche.

## En cas de doute

Si une action a un effet de bord sur l'arbre git, le dépôt distant, la base
de données ou le système de fichiers versionné → tu t'arrêtes et tu demandes.
Le silence n'est pas un oui.`;

/** The full managed block (markers + rules), with a trailing newline. */
export function buildClaudeMdBlock(): string {
  return `${CLAUDE_MD_MARKER_BEGIN}\n\n${GIT_SAFETY_RULES}\n\n${CLAUDE_MD_MARKER_END}`;
}
